import { useState, useEffect } from 'react';
import api from '../services/api';
import { useBranch } from '../context/BranchContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sent by the side bar's Space switcher (hooks/useNavSectors.js) whenever someone switches Space, so the home page can follow it.
export const SPACE_CHANGED_EVENT = 'c30:nav-sector-changed';

/** The Space in use: the one this person chose in the side bar's switcher (kept per branch in this browser) if it is one they are allowed, otherwise their default Space.
 *  Pure, so it can be tested on its own. */
export const pickActiveSpace = (sectors, defaultId, storedId) => {
  const list = Array.isArray(sectors) ? sectors : [];
  return list.find((s) => s.id === storedId) || list.find((s) => s.id === defaultId) || list[0] || null;
};

/**
 * Which dashboard the person's Space goes with ('default' = the current home page, 'sales', 'admin').
 * Only asks the server when `enabled` (the new home page is on for this person). `ready` is false until the answer is in; if the answer cannot be had the view is
 * 'default', the current home page, so a failure never leaves anyone without one.
 */
export const useActiveSpace = (enabled) => {
  const { getEffectiveBranchId } = useBranch();
  const branchId = getEffectiveBranchId();
  const [data, setData] = useState({ sectors: [], defaultId: null, loadedFor: null });
  const [, setSwitchCount] = useState(0);

  useEffect(() => {
    if (!enabled || !branchId) return undefined;
    let cancelled = false;
    api.get(`${API}/nav-header/sectors`, { params: { branch_id: branchId } })
      .then((res) => { if (!cancelled) setData({ sectors: res.data?.sectors || [], defaultId: res.data?.default_sector_id || null, loadedFor: branchId }); })
      .catch(() => { if (!cancelled) setData({ sectors: [], defaultId: null, loadedFor: branchId }); });
    return () => { cancelled = true; };
  }, [enabled, branchId]);

  useEffect(() => {
    const onSwitch = () => setSwitchCount((n) => n + 1);
    window.addEventListener(SPACE_CHANGED_EVENT, onSwitch);
    return () => window.removeEventListener(SPACE_CHANGED_EVENT, onSwitch);
  }, []);

  if (!enabled) return { ready: false, view: 'default', sectorId: null };
  if (!branchId) return { ready: true, view: 'default', sectorId: null };
  let stored = null;
  try { stored = localStorage.getItem(`nav_sector_active_${branchId}`); } catch (_) { /* private mode: the default Space is used */ }
  const active = pickActiveSpace(data.sectors, data.defaultId, stored);
  return { ready: data.loadedFor === branchId, view: active?.dashboard_view || 'default', sectorId: active?.id || null };
};

export default useActiveSpace;
